import { useEffect, useRef, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';

interface Props {
  show: boolean;
  /** Fired when the overlay has finished (~2.2s). */
  onDone: () => void;
  /** Heading shown inside the smoke. */
  title?: string;
  /** Secondary line under the title. */
  subtitle?: string;
}

/* Monochrome plumes — mostly pale smoke, with a single ember layer to give
   the ceremony warmth without breaking the black-and-white vow. */
const PLUMES = [
  { color: '220, 220, 222', size: 420, xStart: '-20vw', xEnd: '-6vw', delay: 0, duration: 2000, intensity: 0.32 },
  { color: '255, 255, 255', size: 520, xStart: '6vw', xEnd: '-2vw', delay: 140, duration: 2100, intensity: 0.22 },
  { color: '194, 58, 46', size: 380, xStart: '22vw', xEnd: '10vw', delay: 280, duration: 2000, intensity: 0.42 },
  { color: '180, 180, 184', size: 320, xStart: '-8vw', xEnd: '8vw', delay: 420, duration: 2200, intensity: 0.3 },
  { color: '240, 240, 242', size: 460, xStart: '18vw', xEnd: '-12vw', delay: 560, duration: 2000, intensity: 0.25 },
];

const TOTAL_MS = 2200;
const REVEAL_AT_MS = 900;

/**
 * Fullscreen smoke ceremony. A handful of huge, blurred colour blobs
 * rise and disperse while a success card surfaces through the haze.
 * Honours prefers-reduced-motion by showing a static card instead.
 */
export function SmokeOverlay({
  show,
  onDone,
  title = 'Бронь отправлена',
  subtitle = 'Ожидайте подтверждения',
}: Props) {
  const [phase, setPhase] = useState<'idle' | 'smoke' | 'reveal' | 'fade'>('idle');
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (!show) {
      setPhase('idle');
      return;
    }

    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce) {
      setPhase('reveal');
      const t = window.setTimeout(() => {
        setPhase('fade');
        window.setTimeout(() => doneRef.current(), 250);
      }, 1200);
      return () => window.clearTimeout(t);
    }

    setPhase('smoke');
    const reveal = window.setTimeout(() => setPhase('reveal'), REVEAL_AT_MS);
    const end = window.setTimeout(() => setPhase('fade'), TOTAL_MS);
    const done = window.setTimeout(() => doneRef.current(), TOTAL_MS + 280);
    return () => {
      window.clearTimeout(reveal);
      window.clearTimeout(end);
      window.clearTimeout(done);
    };
  }, [show]);

  if (!show || phase === 'idle') return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      className="fixed inset-0 z-[200] flex items-center justify-center overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse 120% 80% at 50% 50%, rgba(11, 10, 20, 0.92), rgba(0,0,0,0.98))',
        transition: 'opacity 260ms ease',
        opacity: phase === 'fade' ? 0 : 1,
      }}
    >
      {/* Smoke plumes */}
      {PLUMES.map((p, i) => (
        <span
          key={i}
          aria-hidden
          className="absolute bottom-[-20vh] left-1/2 rounded-full will-change-transform"
          style={
            {
              width: p.size,
              height: p.size,
              marginLeft: -p.size / 2,
              background: `radial-gradient(closest-side, rgba(${p.color}, ${p.intensity + 0.35}), rgba(${p.color}, ${p.intensity}) 55%, transparent 75%)`,
              filter: 'blur(60px)',
              animation: `float-up ${p.duration}ms cubic-bezier(0.22, 1, 0.36, 1) ${p.delay}ms 1 both`,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ['--x0' as any]: p.xStart,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ['--x1' as any]: p.xEnd,
            } as React.CSSProperties
          }
        />
      ))}

      {/* Reveal card */}
      <div
        className="relative z-10 mx-6 max-w-sm text-center rise-in"
        style={{
          opacity: phase === 'reveal' ? 1 : 0,
          transform: phase === 'reveal' ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 420ms ease, transform 520ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div
          className="relative overflow-hidden rounded-3xl bg-white/[0.04] backdrop-blur-2xl px-7 py-8"
          style={{
            border: '1px solid var(--line-2)',
            boxShadow:
              '0 22px 60px -22px rgba(0,0,0,0.9), 0 0 48px -16px rgba(194,58,46,0.3), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl"
            style={{
              background:
                'radial-gradient(closest-side, rgba(194, 58, 46, 0.45), transparent 70%)',
            }}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background:
                'linear-gradient(90deg, transparent 8%, rgba(255,255,255,0.3) 50%, transparent 92%)',
            }}
          />

          <div className="relative flex flex-col items-center gap-4 text-white">
            <span
              className="inline-flex w-14 h-14 items-center justify-center rounded-2xl"
              style={{
                border: '1px solid var(--line-2)',
                background: 'rgba(255,255,255,0.04)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
              }}
            >
              <CheckCircle2 size={26} strokeWidth={1.5} className="text-white" />
            </span>
            <div>
              <div className="headline text-[22px] uppercase tracking-[0.03em]">{title}</div>
              <div className="mt-2 text-[11px] font-mono uppercase tracking-[0.18em] text-white/55">
                {subtitle}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
